"use client";

import type { AppRouter } from "@pkg/api/src/app/router";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { useMemo } from "react";
import superjson from "superjson";
import { useSession } from "../auth/session";
import { getApiUrl } from "../utils";
import { makeQueryClient } from "./query-client";

export const trpc = createTRPCReact<AppRouter>();

let clientQueryClientSingleton: QueryClient;

function getQueryClient() {
	if (typeof window === "undefined") {
		// Server: always make a new query client
		return makeQueryClient();
	}
	// Browser: use singleton pattern to keep the same query client
	if (!clientQueryClientSingleton) {
		clientQueryClientSingleton = makeQueryClient();
	}
	return clientQueryClientSingleton;
}

export function TRPCReactProvider(
	props: Readonly<{
		children: React.ReactNode;
	}>,
) {
	// NOTE: Avoid useState when initializing the query client if you don't
	//       have a suspense boundary between this and the code that may
	//       suspend because React will throw away the client on the initial
	//       render if it suspends and there is no boundary
	const queryClient = getQueryClient();
	const { session, isUserLoading, error } = useSession();

	const trpcClient = useMemo(() => {
		const token = session?.access_token;
		return createTRPCClient<AppRouter>({
			links: [
				httpBatchLink({
					transformer: superjson,
					async headers() {
						return token ? { Authorization: `Bearer ${token}` } : {};
					},
					url: `${getApiUrl()}/trpc`,
					// fetch: env.API_SERVICE.fetch.bind(env.API_SERVICE),
				}),
			],
		});
	}, [session?.access_token]); // Recreate client when token changes

	if (isUserLoading || error) {
		return <>{props.children}</>;
	}

	return (
		<trpc.Provider client={trpcClient} queryClient={queryClient}>
			<QueryClientProvider client={queryClient}>
				{props.children}
			</QueryClientProvider>
		</trpc.Provider>
	);
}
